import React from 'react';
import { 
  File, 
  FileText, 
  Image as FileImage, 
  Music as FileAudio, 
  Video as FileVideo, 
  FileType as FilePdf, 
  Archive as FileArchive, 
  Download 
} from 'lucide-react';

interface FileProps {
  id: string | null;
  url: string;
  name: string | null;
  type: string | null;
  mimeType: string | null;
  size?: number;
}

interface DownloadableFileAttachmentProps {
  file: FileProps;
}

const DownloadableFileAttachment: React.FC<DownloadableFileAttachmentProps> = ({ file }) => {
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = () => {
    const mimeType = file.mimeType?.toLowerCase() || '';
    const fileName = file.name?.toLowerCase() || '';
    
    if (mimeType.includes('image') || fileName.match(/\.(jpg|jpeg|png|gif|webp|svg)$/)) 
      return <FileImage size={20} className="text-blue-500" />;
    
    if (mimeType.includes('audio') || fileName.match(/\.(mp3|wav|ogg|flac)$/)) 
      return <FileAudio size={20} className="text-purple-500" />;
    
    if (mimeType.includes('video') || fileName.match(/\.(mp4|mov|avi|webm)$/)) 
      return <FileVideo size={20} className="text-red-500" />;
    
    if (mimeType.includes('pdf') || fileName.endsWith('.pdf')) 
      return <FilePdf size={20} className="text-orange-500" />;
    
    if (mimeType.includes('zip') || mimeType.includes('rar') || 
        fileName.match(/\.(zip|rar|tar|gz|7z)$/)) 
      return <FileArchive size={20} className="text-amber-500" />;
    
    if (mimeType.includes('text') || fileName.match(/\.(txt|doc|docx|rtf)$/)) 
      return <FileText size={20} className="text-green-500" />;
    
    return <File size={20} className="text-gray-500" />;
  };

  // Extract file extension for display
  const getFileExtension = (filename: string) => {
    return filename.split('.').pop()?.toUpperCase() || '';
  };

  const fileExtension = file.name ? getFileExtension(file.name) : '';

  return (
    <a 
      href={file.url} 
      target="_blank" 
      rel="noopener noreferrer" 
      className="flex items-center p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200 group"
    >
      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 mr-3">
        {getFileIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-800 truncate">
          {file.name || 'Unnamed file'}
        </div>
        <div className="flex items-center text-xs text-gray-500">
          {fileExtension && (
            <span className="uppercase mr-2">{fileExtension}</span>
          )}
          {file.size && (
            <span>{formatFileSize(file.size)}</span>
          )}
        </div>
      </div>
      <div className="ml-2 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity bg-blue-50 text-blue-600">
        <Download size={16} />
      </div>
    </a>
  );
};

export default DownloadableFileAttachment;
